import { formatUsdPerLb, shiftMonth } from "./callForm";
import { monthName, monthOf } from "./format";
import type {
  Call,
  PriceHistory,
  PriceHistoryCall,
  PriceHistoryForecast,
  PricePoint,
} from "./types";

export interface MonthlyCall {
  month: string;
  fmpUsdPerLb: number;
}

export interface PlottedPricePoint {
  month: string;
  positionInMonth: number;
  priceUsdPerLb: number;
  pricePoint: PricePoint;
}

export interface MonthRange {
  fromMonth: string;
  toMonth: string;
}

export function monthsBetween(firstMonth: string, lastMonth: string): string[] {
  const months: string[] = [];
  for (let month = firstMonth; month <= lastMonth; month = shiftMonth(month, 1)) {
    months.push(month);
  }
  return months;
}

export function historyMonths(history: PriceHistory | null, selectedMonth: string): string[] {
  const earliestMonth = history?.earliestMonth ? monthOf(history.earliestMonth) : selectedMonth;
  return monthsBetween(earliestMonth < selectedMonth ? earliestMonth : selectedMonth, selectedMonth);
}

export function clampRange(months: string[], chosenRange: MonthRange | null): MonthRange {
  const firstMonth = months[0];
  const lastMonth = months[months.length - 1];
  if (!chosenRange) return { fromMonth: firstMonth, toMonth: lastMonth };
  const toMonth = chosenRange.toMonth > lastMonth || chosenRange.toMonth < firstMonth ? lastMonth : chosenRange.toMonth;
  const fromMonth =
    chosenRange.fromMonth < firstMonth || chosenRange.fromMonth > toMonth ? firstMonth : chosenRange.fromMonth;
  return { fromMonth, toMonth };
}

export function latestOwnCallFmp(calls: Call[], appUserId: number | null): number | null {
  const latestOwnCall = calls
    .filter((call) => call.enteredByAppUserId === appUserId && call.status === "Active")
    .sort((first, second) => second.recordedUtc.localeCompare(first.recordedUtc))[0];
  const currentMonthPeriod = latestOwnCall?.periods.find((period) => period.periodOffset === 0);
  return currentMonthPeriod?.fmpUsdPerLb ?? null;
}

export function monthlyCallsInRange(calls: PriceHistoryCall[], range: MonthRange): MonthlyCall[] {
  return calls
    .map((call) => ({ month: monthOf(call.month), fmpUsdPerLb: call.fmpUsdPerLb }))
    .filter((monthlyCall) => monthlyCall.month >= range.fromMonth && monthlyCall.month <= range.toMonth);
}

function positionInMonth(isoDate: string): number {
  const [year, monthNumber, day] = isoDate.split("-").map(Number);
  const daysInMonth = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return (day - 1) / daysInMonth;
}

export function plottedPricePoints(pricePoints: PricePoint[], range: MonthRange): PlottedPricePoint[] {
  return pricePoints
    .filter((pricePoint) => pricePoint.status !== "Voided")
    .map((pricePoint) => ({
      month: monthOf(pricePoint.heardDate),
      positionInMonth: positionInMonth(pricePoint.heardDate),
      priceUsdPerLb: pricePoint.priceUsdPerLb,
      pricePoint,
    }))
    .filter((plottedPoint) => plottedPoint.month >= range.fromMonth && plottedPoint.month <= range.toMonth);
}

export interface ForecastMonth {
  month: string;
  fmpUsdPerLb: number;
}

export interface VisibleForecast {
  fromCallMonth: string;
  anchor: MonthlyCall | null;
  months: ForecastMonth[];
}

export function visibleForecast(
  forecast: PriceHistoryForecast | null | undefined,
  monthlyCalls: MonthlyCall[],
  range: MonthRange,
  selectedMonth: string,
): VisibleForecast | null {
  if (!forecast || range.toMonth !== selectedMonth) return null;
  const fromCallMonth = monthOf(forecast.callMonth);
  const months = forecast.periods
    .map((period) => ({ month: monthOf(period.month), fmpUsdPerLb: period.fmpUsdPerLb }))
    .filter((forecastMonth) => forecastMonth.month > range.toMonth);
  if (months.length === 0) return null;
  const anchor = monthlyCalls.find((monthlyCall) => monthlyCall.month === fromCallMonth) ?? null;
  return { fromCallMonth, anchor, months };
}

export const NO_RESIN_CODE = "none";

export interface PricePointFilters {
  priceTypeCode: string | null;
  resinCode: string | null;
}

export const ALL_PRICE_POINTS: PricePointFilters = { priceTypeCode: null, resinCode: null };

export function priceTypeOptions(plottedPoints: PlottedPricePoint[]): string[] {
  return [...new Set(plottedPoints.map((plottedPoint) => plottedPoint.pricePoint.priceTypeCode))].sort();
}

export function resinCodeOptions(plottedPoints: PlottedPricePoint[]): string[] {
  const resinCodes = plottedPoints.map((plottedPoint) => plottedPoint.pricePoint.designationCode ?? NO_RESIN_CODE);
  const namedCodes = [...new Set(resinCodes.filter((resinCode) => resinCode !== NO_RESIN_CODE))].sort();
  return resinCodes.includes(NO_RESIN_CODE) ? [...namedCodes, NO_RESIN_CODE] : namedCodes;
}

export function filterPricePoints(plottedPoints: PlottedPricePoint[], filters: PricePointFilters): PlottedPricePoint[] {
  return plottedPoints.filter((plottedPoint) => {
    const { priceTypeCode, designationCode } = plottedPoint.pricePoint;
    const matchesPriceType = filters.priceTypeCode === null || priceTypeCode === filters.priceTypeCode;
    const matchesResinCode = filters.resinCode === null || (designationCode ?? NO_RESIN_CODE) === filters.resinCode;
    return matchesPriceType && matchesResinCode;
  });
}

function priceCountText(count: number): string {
  return `${count} logged ${count === 1 ? "price" : "prices"}`;
}

export function callVsLoggedPricesReadout(
  resinFamilyCode: string,
  month: string,
  monthlyCalls: MonthlyCall[],
  plottedPoints: PlottedPricePoint[],
): string | null {
  const call = monthlyCalls.find((monthlyCall) => monthlyCall.month === month);
  const pricesThisMonth = plottedPoints
    .filter((plottedPoint) => plottedPoint.month === month)
    .map((plottedPoint) => plottedPoint.priceUsdPerLb);
  const monthLabel = monthName(month);
  if (!call && pricesThisMonth.length === 0) return null;
  if (!call) {
    return `No ${resinFamilyCode} call saved for ${monthLabel} to compare with the ${priceCountText(pricesThisMonth.length)}.`;
  }
  if (pricesThisMonth.length === 0) {
    return `No price points logged in ${monthLabel} to compare your call with.`;
  }
  const averagePrice = pricesThisMonth.reduce((total, price) => total + price, 0) / pricesThisMonth.length;
  const differenceCents = Math.round((call.fmpUsdPerLb - averagePrice) * 1000) / 10;
  const averageText = `the average of ${priceCountText(pricesThisMonth.length)} (${formatUsdPerLb(Math.round(averagePrice * 1000) / 1000)})`;
  const callText = `Your ${monthLabel} call (${formatUsdPerLb(call.fmpUsdPerLb)})`;
  if (differenceCents === 0) return `${callText} matches ${averageText}.`;
  const direction = differenceCents > 0 ? "above" : "below";
  return `${callText} is ${Math.abs(differenceCents)}¢ ${direction} ${averageText}.`;
}

export function callChartX(months: string[], monthlyCall: MonthlyCall): number {
  return months.indexOf(monthlyCall.month);
}

export function pricePointChartX(months: string[], plottedPoint: PlottedPricePoint): number {
  return months.indexOf(plottedPoint.month) - 0.5 + plottedPoint.positionInMonth;
}
